/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { createEventEmitter, emitEvent } from "@adapptio/lib-shared";
import { Dialog } from "../../../dialogs/Dialog";
import { AuthenticateDialogComponent } from "./AuthenticateDialogComponent";
import { API_ERROR_NAME, ApiError } from "../../../api/ApiErrors";

export type AuthStatus = "none" | "loading" | "success" | "failure";
export type AuthError = "invalid_credentials" | "unknown_error";

export interface AuthenticateDialog_Params {}

export interface AuthenticateDialog_Result {}

export class AuthenticateDialog extends Dialog<AuthenticateDialog_Params, AuthenticateDialog_Result> {
	private status: AuthStatus = "none";
	private lastError: AuthError | null = null;

	public readonly onStatusChange = createEventEmitter<void>();

	public getStatus(): AuthStatus {
		return this.status;
	}

	public getLastError(): AuthError | null {
		return this.lastError;
	}

	public authenticate(accessToken: string): void {
		this.status = "loading";
		emitEvent(this.onStatusChange);

		this.dc.authenticate(accessToken).then(() => {
			this.status = "success";
			emitEvent(this.onStatusChange);
			this.close({});
		}).catch((err) => {
			this.status = "failure";

			if (err instanceof ApiError) {
				if (err.name === API_ERROR_NAME.AUTH_INVALID_TOKEN) {
					this.lastError = "invalid_credentials";
				} else {
					this.lastError = "unknown_error";
				}
			} else {
				this.lastError = "unknown_error";
			}
			
			this.dc.get("logger").warn("Authentication failed.", err);
			emitEvent(this.onStatusChange);
		});
	}

	public hasInitialAccessToken(): boolean {
		return !!this.dc.getInitialAccessToken();
	}

	public authenticateWithInitialToken(): void {
		const initialAccessToken = this.dc.getInitialAccessToken();
		
		if (initialAccessToken) {
			this.authenticate(initialAccessToken);
		} else {
			throw new Error("No initial access token available.");
		}
	}

	public getComponent(): typeof AuthenticateDialogComponent {
		return AuthenticateDialogComponent;
	}
}
